import './scss/styles.scss';
import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Alert, Spinner, TabContent, TabPane, Tabs } from '@bphxd/ds-core-react';
import GeoNavbar from './components/geonavbar';
import GeoPlot from './components/plot';
import Plotly from 'plotly.js-basic-dist-min';
import esriConfig from '@arcgis/core/config';
import FeatureConfigs from './Configuration/FeatureConfig';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import { reactPlugin } from './ApplicationInsightsService';
import { operatingLimits } from './constants/limits';
import { unexpectedErrorGraph } from './constants/errors';
import { portalURL } from './constants/config';

type dataLoadStatus = {
  loaded: boolean;
  error: string | null;
};
type loadStatusType = {
  atensionData: dataLoadStatus;
  ptensionData: dataLoadStatus;
  waveData: dataLoadStatus;
  periodWaveData: dataLoadStatus;
};

esriConfig.portalUrl = portalURL;
if (esriConfig.request.interceptors) {
  esriConfig.request.interceptors.push({
    urls: /FeatureServer\/\d+$/,
    after: function (response) {
      response.data.supportedQueryFormats = 'JSON';
    },
  });
}

const webmap = new WebMap({
  portalItem: {
    id: 'de5cb88e98c1441bb635ad3962a39555',
  },
});

const mapView = new MapView({
  zoom: 14,
  map: webmap,
});

function App(props: any) {
  const viewRef = useRef<HTMLDivElement>(null);
  // const [viewType] = React.useState<'2D' | '3D'>('2D');
  // const [viewpoint, setViewpoint] = React.useState<Viewpoint>();
  const [loadStatus, setLoadStatus] = useState<loadStatusType>({
    atensionData: { loaded: false, error: null },
    ptensionData: { loaded: false, error: null },
    waveData: { loaded: false, error: null },
    periodWaveData: { loaded: false, error: null },
  });
  // const [alertCSS, setAlertCSS] = useState('shadow-sm alert alert-solid-info fade show');
  const [active, setActive] = useState('tab-1');
  const [pTensionData, setpTensionData] = useState({});
  const [aTensionData, setaTensionData] = useState({});
  const [waveData, setWaveData] = useState({});
  const [periodWaveData, setPeriodWaveData] = useState({});

  useEffect(() => {
    // setViewpoint(mapView.viewpoint);
    mapView.container = viewRef.current as HTMLDivElement;
    // mapView.viewpoint = viewpoint as Viewpoint;
  }, []);
  useEffect(() => {
    // var inboundForecastedTensionData: __esri.FeatureSet;
    const tensionDates: Date[] = [];
    const tensionLineLoad: string[] = [];
    const normalOperatingLimit: number[] = [];
    const safeOperatingLimit: number[] = [];
    const safeDesignLimit: number[] = [];
    const normalOperatingLimitText: string[] = [];
    const safeOperatingLimitText: string[] = [];
    const safeDesignLimitText: string[] = [];
    const tableLayer = new FeatureLayer({
      url: FeatureConfigs.ForecastedTension,
      objectIdField: 'Id',
    });
    tableLayer
      .load()
      .then(() => {
        tableLayer
          .queryFeatures({
            where: '1=1',
            outFields: ['*'],
            returnGeometry: false,
          })
          .then((results) => {
            // inboundForecastedTensionData = results;
            var lineLength = 0;
            var lineLengthCount = 1;

            if (results.features.length > 0) {
              lineLength = 150;
              //lineLength = lineLength - lineLength * .1;
            }
            results.features.forEach(function (dataProcessing) {
              let d = new Date(dataProcessing.attributes.ForeCastedDate);
              tensionDates.push(d);
              tensionLineLoad.push(dataProcessing.attributes.Tension.toPrecision(2));
              normalOperatingLimit.push(operatingLimits.normalOperatingLimit);
              safeOperatingLimit.push(operatingLimits.safeOperatingLimit);
              safeDesignLimit.push(operatingLimits.safeDesignLimit);
              lineLengthCount++;
              if (lineLengthCount === lineLength) {
                normalOperatingLimitText.push(
                  `${operatingLimits.normalOperatingLimit} - Upper Normal Operating Limit`
                );
                safeOperatingLimitText.push(
                  `${operatingLimits.safeOperatingLimit} - Upper Safe Operating Limit`
                );
                safeDesignLimitText.push(
                  `${operatingLimits.safeDesignLimit} - Upper Safe Design Limit`
                );
              }
              normalOperatingLimitText.push('');
              safeOperatingLimitText.push('');
              safeDesignLimitText.push('');
            });
            const normalOperatingLine = {
              x: tensionDates,
              y: normalOperatingLimit,
              mode: 'lines+text',
              name: 'Normal Limit',
              text: normalOperatingLimitText,
              textposition: 'top',
              type: 'scatter',
            };
            const safeOperatingLine = {
              x: tensionDates,
              y: safeOperatingLimit,
              mode: 'lines+text',
              name: 'Safe Limit',
              text: safeOperatingLimitText,
              textposition: 'top',
              type: 'scatter',
            };
            const safeDesignLine = {
              x: tensionDates,
              y: safeDesignLimit,
              mode: 'lines+text',
              name: 'Design Limit',
              text: safeDesignLimitText,
              textposition: 'top',
              type: 'scatter',
            };
            setpTensionData([
              {
                x: tensionDates,
                y: tensionLineLoad,
                type: 'line+scatter',
                mode: 'line+markers',
                name: 'Forecasted',
                marker: { color: '#007AC9' },
              },
              normalOperatingLine,
              safeOperatingLine,
              safeDesignLine,
            ]);
            setLoadStatus((state: loadStatusType) => ({
              ...state,
              ptensionData: { loaded: true, error: null },
            }));
          })
          .catch(() => {
            setLoadStatus((state: loadStatusType) => ({
              ...state,
              ptensionData: {
                loaded: false,
                error: unexpectedErrorGraph,
              },
            }));
          });
      })
      .catch(() => {
        setLoadStatus((state: loadStatusType) => ({
          ...state,
          ptensionData: {
            loaded: false,
            error: unexpectedErrorGraph,
          },
        }));
      });
  }, []);

  useEffect(() => {
    // var inboundATensionData: __esri.FeatureSet;
    const aTensionDates: Date[] = [];
    const aTensionLineLoad: string[] = [];
    const normalOperatingLimit: number[] = [];
    const safeOperatingLimit: number[] = [];
    const safeDesignLimit: number[] = [];
    const normalOperatingLimitText: string[] = [];
    const safeOperatingLimitText: string[] = [];
    const safeDesignLimitText: string[] = [];
    const aTensionTableLayer = new FeatureLayer({
      url: FeatureConfigs.ActualTension,
      objectIdField: 'Id',
    });
    aTensionTableLayer
      .load()
      .then(() => {
        aTensionTableLayer
          .queryFeatures({
            where: '1=1',
            outFields: ['*'],
            returnGeometry: false,
          })
          .then((results) => {
            var lineLength = 0;
            var lineLengthCount = 1;

            if (results.features.length > 0) {
              lineLength = 150; //(inboundATensionData.features.length / 2);
              //lineLength = lineLength - lineLength * .1;
            }
            results.features.forEach(function (dataProcess) {
              var d = new Date(dataProcess.attributes.ActualTimestamp);
              d.setHours(0, 0, 0, 0);
              aTensionDates.push(d);
              aTensionLineLoad.push(parseFloat(dataProcess.attributes.Tension).toPrecision(2));
            });

            var inboundForecastedTensionData: __esri.FeatureSet;
            var tensionDates: Date[] = [];
            var tensionLineLoad: string[] = [];
            const tableLayer = new FeatureLayer({
              url: FeatureConfigs.ForecastedTension,
              objectIdField: 'Id',
            });
            tableLayer
              .queryFeatures({
                where: '1=1',
                outFields: ['*'],
                returnGeometry: false,
              })
              .then((results) => {
                inboundForecastedTensionData = results;
                if (inboundForecastedTensionData.features.length > 0) {
                  lineLength = 150; //(inboundATensionData.features.length / 2);
                  //lineLength = lineLength - lineLength * .1;
                }
                inboundForecastedTensionData.features.forEach(function (dataProcess) {
                  var d = new Date(dataProcess.attributes.ForeCastedDate);
                  d.setHours(0, 0, 0, 0);
                  tensionDates.push(d);
                  tensionLineLoad.push(dataProcess.attributes.Tension.toPrecision(2));
                  normalOperatingLimit.push(operatingLimits.normalOperatingLimit);
                  safeOperatingLimit.push(operatingLimits.safeOperatingLimit);
                  safeDesignLimit.push(operatingLimits.safeDesignLimit);
                  lineLengthCount++;
                  if (lineLengthCount === lineLength) {
                    normalOperatingLimitText.push(
                      `${operatingLimits.normalOperatingLimit} - Upper Normal Operating Limit`
                    );
                    safeOperatingLimitText.push(
                      `${operatingLimits.safeOperatingLimit} - Upper Safe Operating Limit`
                    );
                    safeDesignLimitText.push(
                      `${operatingLimits.safeDesignLimit} - Upper Safe Design Limit`
                    );
                  }
                  normalOperatingLimitText.push('');
                  safeOperatingLimitText.push('');
                  safeDesignLimitText.push('');
                });
                const normalOperatingLine = {
                  x: tensionDates,
                  y: normalOperatingLimit,
                  mode: 'lines+text',
                  name: 'Normal Limit',
                  text: normalOperatingLimitText,
                  textposition: 'top',
                  type: 'scatter',
                };
                const safeOperatingLine = {
                  x: tensionDates,
                  y: safeOperatingLimit,
                  mode: 'lines+text',
                  name: 'Safe Limit',
                  text: safeOperatingLimitText,
                  textposition: 'top',
                  type: 'scatter',
                };
                const safeDesignLine = {
                  x: tensionDates,
                  y: safeDesignLimit,
                  mode: 'lines+text',
                  name: 'Design Limit',
                  text: safeDesignLimitText,
                  textposition: 'top',
                  type: 'scatter',
                };
                setaTensionData([
                  {
                    x: aTensionDates,
                    y: aTensionLineLoad,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Actual',
                    marker: { color: '#FF9900' },
                  },
                  {
                    x: tensionDates,
                    y: tensionLineLoad,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Forecasted',
                    marker: { color: '#007AC9' },
                  },
                  normalOperatingLine,
                  safeOperatingLine,
                  safeDesignLine,
                ]);
                setLoadStatus((state: loadStatusType) => ({
                  ...state,
                  atensionData: {
                    loaded: true,
                    error: null,
                  },
                }));
              })
              .catch(() => {
                setLoadStatus((state: loadStatusType) => ({
                  ...state,
                  atensionData: {
                    loaded: false,
                    error: unexpectedErrorGraph,
                  },
                }));
              });
          });
      })
      .catch(() => {
        setLoadStatus((state: loadStatusType) => ({
          ...state,
          atensionData: {
            loaded: false,
            error: unexpectedErrorGraph,
          },
        }));
      });
  }, []);

  useEffect(() => {
    // var inboundAWaveHeightData: __esri.FeatureSet;
    const aWaveDates: Date[] = [];
    const aWaveMeters: string[] = [];
    const aWaveTableLayer = new FeatureLayer({
      url: FeatureConfigs.ActualWaveHeight,
      objectIdField: 'Id',
    });
    aWaveTableLayer
      .load()
      .then(() => {
        aWaveTableLayer
          .queryFeatures({
            where: '1=1',
            outFields: ['*'],
            returnGeometry: false,
          })
          .then((results) => {
            results.features.forEach(function (dataProcess) {
              let d = new Date(dataProcess.attributes.DateMonitored);
              d.setHours(0, 0, 0, 0);
              aWaveDates.push(d);
              aWaveMeters.push(parseFloat(dataProcess.attributes.WaveHeight).toPrecision(2));
            });

            // var inboundpWaveHeightData: __esri.FeatureSet;
            const pWaveDates: Date[] = [];
            const pWaveMeters: string[] = [];
            const tableLayer = new FeatureLayer({
              url: FeatureConfigs.ForecastedWaveHeightPeriod,
              objectIdField: 'Id',
            });
            tableLayer
              .queryFeatures({
                where: '1=1',
                outFields: ['*'],
                returnGeometry: false,
              })
              .then((results) => {
                results.features.forEach(function (dataProcess) {
                  let d = new Date(dataProcess.attributes.ForecastedDate);
                  d.setHours(0, 0, 0, 0);
                  pWaveDates.push(d);
                  pWaveMeters.push(dataProcess.attributes.SWHeightMeter.toPrecision(2));
                });
                setWaveData([
                  {
                    x: aWaveDates,
                    y: aWaveMeters,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Actual',
                    marker: { color: '#FF9900' },
                  },
                  {
                    x: pWaveDates,
                    y: pWaveMeters,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Forecasted',
                    marker: { color: '#007AC9' },
                  },
                ]);
                setLoadStatus((state: loadStatusType) => ({
                  ...state,
                  waveData: {
                    loaded: true,
                    error: null,
                  },
                }));
              });
          })
          .catch(() => {
            setLoadStatus((state: loadStatusType) => ({
              ...state,
              waveData: {
                loaded: false,
                error: unexpectedErrorGraph,
              },
            }));
          });
      })
      .catch(() => {
        setLoadStatus((state: loadStatusType) => ({
          ...state,
          waveData: {
            loaded: false,
            error: unexpectedErrorGraph,
          },
        }));
      });
  }, []);
  useEffect(() => {
    // var inboundAWavePeriodData: __esri.FeatureSet;
    const aWaveDates: Date[] = [];
    const aWaveSeconds: string[] = [];
    const aWaveTableLayer = new FeatureLayer({
      url: FeatureConfigs.ActualWavePeriod,
      objectIdField: 'Id',
    });
    aWaveTableLayer
      .load()
      .then(() => {
        aWaveTableLayer
          .queryFeatures({
            where: '1=1',
            outFields: ['*'],
            returnGeometry: false,
          })
          .then((results) => {
            results.features.forEach(function (dataProcess) {
              const d = new Date(dataProcess.attributes.DateMonitored);
              d.setHours(0, 0, 0, 0);
              aWaveDates.push(d);
              aWaveSeconds.push(parseFloat(dataProcess.attributes.WavePeriod).toPrecision(2));
            });

            // var inboundpWavePeriodData: __esri.FeatureSet;
            const pWaveDates: Date[] = [];
            const pWaveSeconds: string[] = [];
            const tableLayer = new FeatureLayer({
              url: FeatureConfigs.ForecastedWaveHeightPeriod,
              objectIdField: 'Id',
            });
            tableLayer
              .queryFeatures({
                where: '1=1',
                outFields: ['*'],
                returnGeometry: false,
              })
              .then((results) => {
                // inboundpWavePeriodData = results;
                results.features.forEach(function (dataProcess) {
                  const d = new Date(dataProcess.attributes.ForecastedDate);
                  d.setHours(0, 0, 0, 0);
                  pWaveDates.push(d);
                  pWaveSeconds.push(dataProcess.attributes.WPSeconds.toPrecision(2));
                });
                setPeriodWaveData([
                  {
                    x: aWaveDates,
                    y: aWaveSeconds,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Actual',
                    marker: { color: '#FF9900' },
                  },
                  {
                    x: pWaveDates,
                    y: pWaveSeconds,
                    type: 'line+scatter',
                    mode: 'line+markers',
                    name: 'Forecasted',
                    marker: { color: '#007AC9' },
                  },
                ]);
                setLoadStatus((state: loadStatusType) => ({
                  ...state,
                  periodWaveData: {
                    loaded: true,
                    error: null,
                  },
                }));
              });
          })
          .catch(() => {
            setLoadStatus((state: loadStatusType) => ({
              ...state,
              periodWaveData: {
                loaded: false,
                error: unexpectedErrorGraph,
              },
            }));
          });
      })
      .catch(() => {
        setLoadStatus((state: loadStatusType) => ({
          ...state,
          periodWaveData: {
            loaded: false,
            error: unexpectedErrorGraph,
          },
        }));
      });
  }, []);

  const handleClick = (event: any) => {
    setActive(event.target.id);
  };
  const chartStyle = { width: '100%', height: '100%', autosize: true };
  const chartLayoutWLP = { title: 'Significant Wave Height Hs (m)' };
  const chartLayoutWP = { title: 'Wave Period Tp (s)' };

  const chartLayoutTension = {
    title: 'Max Tension (tonnes)',
    tickangle: -45,
    font: {
      size: 11,
    },
  };
  useEffect(() => {
    Plotly.Plots.resize(active);
  }, [active]);
  return (
    <div className="box">
      <AppInsightsContext.Provider value={reactPlugin}>
        <GeoNavbar username={props.user} />

        <div className="container-fluid content">
          <div className="row h-100">
            <div className="d-flex w-100">
              <Tabs tabStyle="elevated" vertical>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-1"
                    className={`tab-button ${active === 'tab-1' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Peak Mooring Load Predictions"
                    data-parentid="Tab Button"
                  >
                    Peak Mooring Load Predictions
                  </Tabs.Link>
                </Tabs.Item>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-2"
                    className={`tab-button ${active === 'tab-2' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Actual Tension vs. Predicted Tension"
                    data-parentid="Tab Button"
                  >
                    Actual Tension vs. Predicted Tension
                  </Tabs.Link>
                </Tabs.Item>

                <Tabs.Item>
                  <Tabs.Link
                    id="tab-3"
                    className={`tab-button ${active === 'tab-3' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Wave Heights"
                    data-parentid="Tab Button"
                  >
                    Wave Heights
                  </Tabs.Link>
                </Tabs.Item>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-4"
                    className={`tab-button ${active === 'tab-4' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Wave Periods"
                    data-parentid="Tab Button"
                  >
                    Wave Periods
                  </Tabs.Link>
                </Tabs.Item>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-5"
                    className={`tab-button ${active === 'tab-5' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Map Tab"
                    data-parentid="Tab Button"
                  >
                    Map
                  </Tabs.Link>
                </Tabs.Item>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-6"
                    className={`tab-button ${active === 'tab-6' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Data Sources"
                    data-parentid="Tab Button"
                  >
                    Data Sources
                  </Tabs.Link>
                </Tabs.Item>
                <Tabs.Item>
                  <Tabs.Link
                    id="tab-7"
                    className={`tab-button ${active === 'tab-7' ? 'active' : ''}`}
                    href="#"
                    onClick={handleClick}
                    data-id="Process"
                    data-parentid="tab-button"
                  >
                    Process
                  </Tabs.Link>
                </Tabs.Item>
              </Tabs>
              <TabContent activeTab={active} vertical className="p-4 w-100">
                <TabPane tabId="tab-1">
                  <p className="info-header">
                    A graph showing forecasted GTA FLNG mooring loads. Forecasts are calculated
                    based on wave heights & periods taken from the bp ECMWF derived weather
                    forecast.
                  </p>
                  {loadStatus.ptensionData.loaded ? (
                    <GeoPlot
                      data={pTensionData}
                      style={chartStyle}
                      layout={chartLayoutTension}
                      id={'tab-1'}
                      className={active}
                    />
                  ) : !loadStatus.ptensionData.error ? (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Alert color="danger" iconStrokeWidth={2} shadow>
                        {loadStatus.ptensionData.error}
                      </Alert>
                    </div>
                  )}
                </TabPane>
                <TabPane tabId="tab-2">
                  <p className="info-header">
                    {' '}
                    <p>
                      A graph showing forecasted GTA FLNG mooring loads against the actually
                      observed stress values from the GTA FLNG mooring line tensioners.
                    </p>
                  </p>
                  {loadStatus.atensionData.loaded ? (
                    <GeoPlot
                      data={aTensionData}
                      style={chartStyle}
                      layout={chartLayoutTension}
                      id={'tab-2'}
                      className={active}
                    />
                  ) : !loadStatus.atensionData.error ? (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Alert color="danger" iconStrokeWidth={2} shadow>
                        {loadStatus.atensionData.error}
                      </Alert>
                    </div>
                  )}
                </TabPane>
                <TabPane tabId="tab-3">
                  <p className="info-header">
                    Observed significant wave heights at the GTA Hub location. Forecasted wave
                    heights are taken from the bp weather forecast (ECMWF derived) with observed
                    wave heights coming from the GTA Hub's weather station.
                  </p>
                  {loadStatus.waveData.loaded ? (
                    <GeoPlot
                      data={waveData}
                      style={chartStyle}
                      layout={chartLayoutWLP}
                      id={'tab-3'}
                      className={active}
                    />
                  ) : !loadStatus.waveData.error ? (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Alert color="danger" iconStrokeWidth={2} shadow>
                        {loadStatus.waveData.error}
                      </Alert>
                    </div>
                  )}
                </TabPane>
                <TabPane tabId="tab-4">
                  <p className="info-header">
                    A graph showing forecasted vs observed wave period at the GTA Hub location.
                    Forecasted wave period values are taken from the bp Weather Forecast (ECMWF
                    derived) with observed wave period coming from the GTA Hub's weather station.
                  </p>
                  {loadStatus.periodWaveData.loaded ? (
                    <GeoPlot
                      data={periodWaveData}
                      style={chartStyle}
                      layout={chartLayoutWP}
                      id={'tab-4'}
                      className={active}
                    />
                  ) : !loadStatus.periodWaveData.error ? (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Alert color="danger" iconStrokeWidth={2} shadow>
                        {loadStatus.periodWaveData.error}
                      </Alert>
                    </div>
                  )}
                </TabPane>

                <TabPane tabId="tab-5">
                  <div id="viewDiv" data-id="Map Click" data-parentid="Map" ref={viewRef}></div>
                </TabPane>
                <TabPane tabId="tab-6">
                  <h6 className="underline">Data Sources</h6>
                  <p>
                    The GTA Down-Manning tool uses the following data sources to calculate and
                    validate its forecasts:
                  </p>
                  <ul className="bullet">
                    <li>
                      <p>
                        <span className="bold">bp Weather Forecast</span> – Mean Wave Period and
                        Significant Wave Height values are taken from the ECMWF derived bp forecast.
                      </p>
                      <p>
                        The weather forecast is updated twice daily by the bp Weather Forecast team
                        (2am/2pm UTC).{' '}
                      </p>
                      <p>
                        The forecast for the location ‘BP_TORTUE_HUB_LOCATION’ is being used by the
                        tool.{' '}
                      </p>
                    </li>
                    <li>
                      <p>
                        <span className="bold">Observed Mooring Line Tensions</span> – Observed
                        stress in the GIMI FLNG mooring line tensioners are pulled from the Pi data
                        lake.
                      </p>
                      <p>
                        A stress value for each of the tensioners is pulled from the Pi data lake
                        continuous stream every 15mins. Twice a day this filtered data stream is
                        passed across to the Down-Manning tool for visualisation.{' '}
                      </p>
                    </li>
                    <li>
                      <p>
                        <span className="bold">Observed GTA Hub Weather </span> – Observed Wave
                        Periods and Significant Wave Heights are taken from the GTA Hubs weather
                        station.
                      </p>
                      <p>
                        Values are pulled from the Pi data lake continuous stream every 15mins.
                        Twice a day this filtered data stream is passed across to the Down-Manning
                        tool for visualisation.{' '}
                      </p>
                    </li>
                  </ul>
                </TabPane>
                <TabPane tabId="tab-7">
                  <h6 className="underline">Process</h6>
                  <p>
                    The seven-day forecasted max mooring line loads are based on a function (HsTp2)
                    of the forecasted Mean Wave Period (Tp) and the Significant Wave Height (Hs).{' '}
                  </p>
                  <p>
                    The forecasted Mean Wave Period and the Significant Wave Height are taken from
                    the bp Weather Forecast for the location ‘BP_TORTUE_HUB_LOCATION’.{' '}
                  </p>
                  <p>
                    The result of this function is applied against a reference table to provide a
                    loading prediction.{' '}
                  </p>
                  <p>
                    Alerts are prompted by forecasted loads crossing any of the three below limits:{' '}
                  </p>
                  <table>
                    <tr>
                      <th>Limit Name</th>
                      <th>Forecasted Line Load (Tonnes) </th>
                    </tr>
                    <tr>
                      <td>Upper Normal Operating Limit </td>
                      <td>{operatingLimits.normalOperatingLimit}</td>
                    </tr>
                    <tr>
                      <td>Upper Safe Operating Limit </td>
                      <td>{operatingLimits.safeOperatingLimit}</td>
                    </tr>
                    <tr>
                      <td>Upper Safe Design Limit </td>
                      <td>{operatingLimits.safeDesignLimit}</td>
                    </tr>
                  </table>
                  <p>
                    Alerts are sent to the
                    <a href="mailto:gtaweatheralerts@bp.com?subject=GTA Down-Manning Support Enquiry">
                      {' '}
                      gtaweatheralerts@bp.com{' '}
                    </a>
                    distribution list, contact Paul Henley (
                    <a href="mailto:paul.henley1@bp.com?subject=GTA Down-Manning Support Enquiry">
                      paul.henley1@bp.com
                    </a>{' '}
                    ) for more information about being added to this distribution list.{' '}
                  </p>
                </TabPane>
              </TabContent>
            </div>
          </div>
        </div>

        <footer className="footer">
          <p className="text-center text-gray-500 text-xs/loose font-size-8">
            <i>
              Decision Support Tool Supporting Notes and Guidance and
              <a
                className="underline"
                target="_blank"
                href="https://bp365.sharepoint.com/sites/Intranet-Governanceandrequirements/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FIntranet%2DGovernanceandrequirements%2FShared%20Documents%2Fbp%20requirements%2FFind%20bp%20requirements%2FInnovation%20%26%20engineering%2FI%26E%20Requirements%20and%20Processes%20Library%2FIT%20Policy%2Fbp%20Policy%20%2D%20Protecting%20Our%20Information%20399999%2Epdf&amp;parent=%2Fsites%2FIntranet%2DGovernanceandrequirements%2FShared%20Documents%2Fbp%20requirements%2FFind%20bp%20requirements%2FInnovation%20%26%20engineering%2FI%26E%20Requirements%20and%20Processes%20Library%2FIT%20Policy"
                rel="noreferrer"
                data-id="Policy Link"
                data-parentid="Link"
              >
                {' '}
                bp Policy Protecting our Information
              </a>
              . Output may be inaccurate. Its use and output may be monitored for quality assurance
              and security purposes.
            </i>
          </p>
        </footer>
      </AppInsightsContext.Provider>
    </div>
  );
}
export default App;
